import React, { useState, useRef, useEffect } from "react";

import "./EasingEditor.css";

interface ControlPoint {
  x: number;
  y: number;
}

interface EasingEditorProps {
  onSave?: (easing: string, name: string) => void;
  onClose?: () => void;
  /** the clip's current easing — a cubic-bezier(...) value seeds the curve so
   * an existing custom ease can be re-edited instead of starting over */
  initialEasing?: string;
}

const parseCubicBezier = (
  easing: string | undefined,
): { p1: ControlPoint; p2: ControlPoint } | null => {
  const match = easing?.match(/^cubic-bezier\(([^)]+)\)$/);
  if (!match) {
    return null;
  }
  const nums = match[1].split(",").map((n) => Number(n.trim()));
  if (nums.length !== 4 || nums.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return { p1: { x: nums[0], y: nums[1] }, p2: { x: nums[2], y: nums[3] } };
};

const PRESET_CURVES: Record<string, { p1: ControlPoint; p2: ControlPoint }> = {
  linear: { p1: { x: 0, y: 0 }, p2: { x: 1, y: 1 } },
  ease: { p1: { x: 0.25, y: 0.1 }, p2: { x: 0.25, y: 1 } },
  "ease-in": { p1: { x: 0.42, y: 0 }, p2: { x: 1, y: 1 } },
  "ease-out": { p1: { x: 0, y: 0 }, p2: { x: 0.58, y: 1 } },
  "ease-in-out": { p1: { x: 0.42, y: 0 }, p2: { x: 0.58, y: 1 } },
  "back-out": { p1: { x: 0.34, y: 1.56 }, p2: { x: 0.64, y: 1 } },
  "back-in": { p1: { x: 0.36, y: 0 }, p2: { x: 0.66, y: -0.56 } },
  overshoot: { p1: { x: 0.5, y: 1.6 }, p2: { x: 0.7, y: 1 } },
  anticipate: { p1: { x: 0.6, y: -0.3 }, p2: { x: 0.4, y: 1.3 } },
};

const GRAPH_WIDTH = 360;
const GRAPH_HEIGHT = 360;
const PADDING_X = 40;
// vertical headroom above/below [0,1] sized so the drag clamp below AND the
// strongest presets (overshoot y=1.6, back-in y=-0.56) stay inside the
// viewBox — a handle outside it would be invisible and ungrabbable
const PADDING_Y = 105;
const PLOT_W = GRAPH_WIDTH - PADDING_X * 2;
const PLOT_H = GRAPH_HEIGHT - PADDING_Y * 2;

// x: [0,1] across the inner plot; y: 0 at bottom, 1 at top.
const pointToSvg = (p: ControlPoint) => ({
  x: PADDING_X + p.x * PLOT_W,
  y: PADDING_Y + (1 - p.y) * PLOT_H,
});

const svgToPoint = (x: number, y: number): ControlPoint => ({
  // CSS cubic-bezier requires x in [0,1]; y may overshoot.
  x: Math.max(0, Math.min(1, (x - PADDING_X) / PLOT_W)),
  // [-0.6, 1.6] maps to within PADDING_Y of the plot, keeping handles visible
  y: Math.max(-0.6, Math.min(1.6, 1 - (y - PADDING_Y) / PLOT_H)),
});

export const EasingEditor: React.FC<EasingEditorProps> = ({
  onSave,
  onClose,
  initialEasing,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const initial =
    parseCubicBezier(initialEasing) ?? PRESET_CURVES["ease-in-out"];
  const [p1, setP1] = useState<ControlPoint>(initial.p1);
  const [p2, setP2] = useState<ControlPoint>(initial.p2);
  const [dragging, setDragging] = useState<"p1" | "p2" | null>(null);
  const [duration, setDuration] = useState(1000);
  const [customName, setCustomName] = useState("custom-easing");
  const [previewKey, setPreviewKey] = useState(0);

  const startDrag = (point: "p1" | "p2") => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // without capture, releasing the button outside the browser window never
    // fires pointerup and the handle stays glued to the cursor on re-entry
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    setDragging(point);
  };

  useEffect(() => {
    if (!dragging) {
      return;
    }
    const handlePointerMove = (e: PointerEvent) => {
      if (!svgRef.current) {
        return;
      }
      const rect = svgRef.current.getBoundingClientRect();
      // account for the svg being scaled to fit its box
      const sx = ((e.clientX - rect.left) / rect.width) * GRAPH_WIDTH;
      const sy = ((e.clientY - rect.top) / rect.height) * GRAPH_HEIGHT;
      const next = svgToPoint(sx, sy);
      (dragging === "p1" ? setP1 : setP2)(next);
    };
    const handlePointerUp = () => setDragging(null);
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("pointercancel", handlePointerUp);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [dragging]);

  const p1Svg = pointToSvg(p1);
  const p2Svg = pointToSvg(p2);
  const origin = pointToSvg({ x: 0, y: 0 });
  const target = pointToSvg({ x: 1, y: 1 });

  // The real cubic-bezier curve, drawn natively by SVG — same math the browser
  // uses for cubic-bezier(), so what you see is what plays.
  const curvePath = `M${origin.x},${origin.y} C${p1Svg.x},${p1Svg.y} ${p2Svg.x},${p2Svg.y} ${target.x},${target.y}`;

  const easingString = `cubic-bezier(${p1.x.toFixed(3)}, ${p1.y.toFixed(
    3,
  )}, ${p2.x.toFixed(3)}, ${p2.y.toFixed(3)})`;

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  // which preset (if any) the current curve matches — else "custom"
  const activePreset =
    Object.entries(PRESET_CURVES).find(
      ([, c]) =>
        c.p1.x === p1.x &&
        c.p1.y === p1.y &&
        c.p2.x === p2.x &&
        c.p2.y === p2.y,
    )?.[0] ?? "custom";

  return (
    <div className="easing-editor" onMouseDown={(e) => e.stopPropagation()}>
      <div className="easing-editor-title">
        <span>Custom easing</span>
        <button
          type="button"
          className="easing-editor-close"
          onClick={onClose}
          title="Close editor"
        >
          ✕
        </button>
      </div>

      {/* Preset dropdown */}
      <label className="easing-preset-row">
        <span>Preset</span>
        <select
          className="easing-preset-select"
          value={activePreset}
          onChange={(e) => {
            const curve = PRESET_CURVES[e.target.value];
            if (curve) {
              setP1(curve.p1);
              setP2(curve.p2);
            }
          }}
        >
          {activePreset === "custom" && <option value="custom">Custom</option>}
          {Object.keys(PRESET_CURVES).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>

      {/* Graph */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
        className="easing-curve-svg"
      >
        {gridLines.map((v) => {
          const gx = pointToSvg({ x: v, y: 0 }).x;
          const gy = pointToSvg({ x: 0, y: v }).y;
          return (
            <g key={v} className="grid">
              <line
                x1={gx}
                y1={pointToSvg({ x: 0, y: 1 }).y}
                x2={gx}
                y2={origin.y}
              />
              <line x1={origin.x} y1={gy} x2={target.x} y2={gy} />
            </g>
          );
        })}

        <line
          className="handle-line"
          x1={origin.x}
          y1={origin.y}
          x2={p1Svg.x}
          y2={p1Svg.y}
        />
        <line
          className="handle-line"
          x1={target.x}
          y1={target.y}
          x2={p2Svg.x}
          y2={p2Svg.y}
        />

        <path className="curve" d={curvePath} />

        <circle
          className="handle"
          cx={p1Svg.x}
          cy={p1Svg.y}
          r="9"
          onPointerDown={startDrag("p1")}
        />
        <circle
          className="handle"
          cx={p2Svg.x}
          cy={p2Svg.y}
          r="9"
          onPointerDown={startDrag("p2")}
        />
      </svg>

      {/* Live preview + speed */}
      <div
        className="preview-track"
        key={previewKey}
        style={
          {
            "--easing": easingString,
            "--duration": `${duration}ms`,
          } as React.CSSProperties
        }
      >
        <div className="preview-dot" />
      </div>
      <div className="preview-controls">
        <span>Speed</span>
        <input
          type="range"
          min="200"
          max="3000"
          step="50"
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
        />
        <button
          className="preview-restart"
          onClick={() => setPreviewKey((k) => k + 1)}
        >
          ⟲
        </button>
      </div>

      {/* Footer */}
      <div className="easing-editor-footer">
        <input
          className="easing-name-input"
          type="text"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          placeholder="name this easing"
        />
        <button className="btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-save"
          onClick={() =>
            onSave?.(easingString, customName.trim() || "custom-easing")
          }
        >
          Save &amp; Use
        </button>
      </div>
    </div>
  );
};
